<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { patientsApi, type PatientForGroupsManagement } from '@/api/patients';
import { groupsApi } from '@/api/groups';
import { useTableFilter } from '@/composables/useTableFilter';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import BulkAssignDialog from './bulk-assign-dialog.vue';
import GroupMembershipsEditDialog from './group-memberships-edit-dialog.vue';

const HTTP_UNPROCESSABLE_CONTENT = 422;

const queryClient = useQueryClient();
const search = ref('');
const groupIdIn = ref<number[]>([]);
const showUngrouped = ref(false);
const selectedIds = ref<number[]>([]);
const dialogOpen = ref(false);
const editing = ref<{ id: number; name: string; groupIds: number[] } | null>(null);
const editDialogOpen = ref(false);
const editErrorMessage = ref<string | null>(null);

const { data: groups } = useQuery({
  queryKey: ['groups', 'forFilters'],
  queryFn: () => groupsApi.indexForFilters(),
  initialData: [],
});

const queryKey = computed(() => [
  'settings', 'groupsManagement', 'patients', groupIdIn.value, showUngrouped.value,
]);

const { data: patients, isPending } = useQuery({
  queryKey,
  queryFn: () => patientsApi.indexForGroupsManagement(groupIdIn.value, showUngrouped.value),
  initialData: [] as PatientForGroupsManagement[],
});

const groupNameById = computed(() => {
  const map: Record<number, string> = {};
  (groups.value ?? []).forEach((g) => {
    map[g.id] = g.name;
  });
  return map;
});

const customFilter = useTableFilter(['firstName', 'lastName', 'dni', 'idCode']);

const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });

const headers = [
  { title: '', key: 'data-table-select', sortable: false, width: 48 },
  { title: 'Nombre', key: 'fullName', sortable: true, width: '35%', cellProps: { class: 'text-truncate' } },
  { title: 'RUT', key: 'dni', width: '25%' },
  { title: 'Grupos', key: 'groups', sortable: false, width: '30%' },
  { title: '', key: 'actions', sortable: false, width: 56, align: 'center' as const },
];

function patientGroupIds(patient: PatientForGroupsManagement): number[] {
  return patient.groupIds ?? [];
}

function openEdit(patient: PatientForGroupsManagement) {
  editing.value = {
    id: patient.id,
    name: patient.fullName,
    groupIds: patientGroupIds(patient),
  };
  editErrorMessage.value = null;
  editDialogOpen.value = true;
}

const { mutate: saveMemberships, isPending: isSaving } = useMutation<unknown, AxiosError, number[]>({
  mutationFn: (groupIds) => {
    if (!editing.value) return Promise.reject(new Error('No patient is currently being edited'));
    return patientsApi.updateGroups(editing.value.id, groupIds);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['settings', 'groupsManagement'] });
    editDialogOpen.value = false;
  },
  onError: (error) => {
    editErrorMessage.value = error.response?.status === HTTP_UNPROCESSABLE_CONTENT ?
      'Selecciona al menos un grupo.' :
      'No se pudieron guardar los grupos. Inténtalo de nuevo.';
  },
});

function openBulkAssign() {
  if (selectedIds.value.length === 0) return;
  dialogOpen.value = true;
}

function onAssigned() {
  selectedIds.value = [];
}
</script>

<template>
  <v-data-table
    v-bind="tableProps"
    v-model="selectedIds"
    :headers="headers"
    :header-props="{ class: 'font-weight-bold' }"
    :items="patients"
    :search="search"
    :custom-filter="customFilter"
    :loading="isPending"
    loading-text="Cargando pacientes..."
    item-value="id"
    show-select
    density="comfortable"
  >
    <template #top>
      <v-toolbar
        color="white"
        flat
      >
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="selectedIds.length === 0"
          prepend-icon="mdi-folder-plus-outline"
          class="mr-4"
          @click="openBulkAssign"
        >
          Asignar a grupo
        </v-btn>
      </v-toolbar>
      <v-toolbar
        color="white"
        flat
      >
        <v-spacer />
        <v-text-field
          v-model="search"
          label="Buscar"
          class="mx-4 mb-4 v-col-4"
          prepend-inner-icon="mdi-magnify"
          single-line
          variant="underlined"
          hide-details
        />
        <group-filter-select
          v-model="groupIdIn"
          :disabled="showUngrouped"
          class="pb-1 mr-4 v-col-3"
        />
        <v-checkbox
          v-model="showUngrouped"
          label="Sin grupo"
          color="primary"
          density="compact"
          hide-details
          class="mr-4"
        />
      </v-toolbar>
    </template>

    <template #[`item.fullName`]="{ item }">
      {{ item.firstName }} {{ item.lastName }}
    </template>

    <template #[`item.groups`]="{ item }">
      <span
        v-if="patientGroupIds(item).length === 0"
        class="text-medium-emphasis text-caption"
      >
        Sin grupo
      </span>
      <v-chip
        v-for="gid in patientGroupIds(item)"
        :key="gid"
        size="small"
        class="me-1 my-1"
      >
        {{ groupNameById[gid] || `#${gid}` }}
      </v-chip>
    </template>

    <template #[`item.actions`]="{ item }">
      <v-btn
        icon="mdi-pencil"
        variant="text"
        size="small"
        density="comfortable"
        aria-label="Editar grupos"
        @click="openEdit(item)"
      />
    </template>
  </v-data-table>

  <bulk-assign-dialog
    v-model="dialogOpen"
    member-type="Patient"
    :member-ids="selectedIds"
    @assigned="onAssigned"
  />

  <group-memberships-edit-dialog
    v-model="editDialogOpen"
    :member-name="editing?.name ?? ''"
    :initial-group-ids="editing?.groupIds ?? []"
    :loading="isSaving"
    :error-message="editErrorMessage"
    @save="(groupIds) => saveMemberships(groupIds)"
    @clear-error="editErrorMessage = null"
  />
</template>

<style scoped>
:deep(.v-data-table__table) {
  table-layout: fixed;
}
</style>
